import { Stack } from '@mui/material';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

// Exact ray outlines from LuminarySpinner.svg, PR #13752 at 9f89a147.
export const sunburstRays = [
  'M12.0851 12L14.4305 12.4713L24.1671 12.5053L24.1702 11.5781L14.4336 11.5441Z',
  'M12.0851 12L14.4336 11.5441L23.4418 7.87534L23.087 7.01919L14.0788 10.688Z',
  'M12.0851 12L14.0788 10.688L20.989 3.87276L20.3291 3.21751L13.422 10.0296Z',
  'M12.0851 12L13.422 10.0296L17.1791 1.10959L16.3168 0.751062L12.5598 9.67109Z',
  'M12.0851 12L12.5598 9.67109L12.594 0.00309079L11.6602 0L11.626 9.668Z',
  'M12.0851 12L11.626 9.668L7.93119 0.723245L7.06897 1.0756L10.7638 10.0203Z',
  'M12.0851 12L10.7638 10.0203L3.90023 3.15879L3.24033 3.81404L10.1008 10.6725Z',
  'M12.0851 12L10.1008 10.6725L1.11746 6.94192L0.756389 7.79807L9.73968 11.5287Z',
  'M12.0851 12L9.73968 11.5287L0.00311271 11.4947L0 12.4219L9.73656 12.4559Z',
  'M12.0851 12L9.73656 12.4559L0.728375 16.1247L1.08322 16.9839L10.0883 13.3151Z',
  'M12.0851 12L10.0883 13.3151L3.18119 20.1272L3.84109 20.7825L10.7482 13.9704Z',
  'M12.0851 12L10.7482 13.9704L6.99115 22.8904L7.85337 23.2489L11.6104 14.3289Z',
  'M12.0851 12L11.6104 14.3289L11.5762 23.9969L12.51 24L12.5442 14.332Z',
  'M12.0851 12L12.5442 14.332L16.239 23.2768L17.1044 22.9244L13.4065 13.9797Z',
  'M12.0851 12L13.4065 13.9797L20.27 20.8412L20.933 20.186L14.0695 13.3275Z',
  'M12.0851 12L14.0695 13.3275L23.0527 17.0581L23.4138 16.2019L14.4305 12.4713Z',
];

interface DocumentLoaderProps {
  // Page rendering and metadata-only panels have no measurable percentage.
  indeterminate?: boolean;
  ready?: boolean;
  onComplete?: () => void;
}

interface MotionState {
  time: number;
  completedAt: number | null;
  reduced?: boolean;
}
const MotionContext = createContext<React.RefObject<MotionState> | null>(null);

// Keep the entrance continuous across the module, query, and token loaders.
export function DocumentLoaderProvider({
  children,
  documentId,
}: PropsWithChildren<{ documentId?: string }>) {
  const parentClock = useContext(MotionContext);
  const clock = useRef<MotionState>({
    time: 0,
    completedAt: null,
  });
  const previousDocumentId = useRef(documentId);
  // Only the outer provider owns identity. Resolving the first ID continues
  // the query's entrance; selecting a different document starts a fresh clock.
  if (!parentClock && documentId !== undefined) {
    if (
      previousDocumentId.current !== undefined &&
      previousDocumentId.current !== documentId
    ) {
      clock.current = { time: 0, completedAt: null };
    }
    previousDocumentId.current = documentId;
  }
  return (
    <MotionContext.Provider value={parentClock ?? clock}>
      {children}
    </MotionContext.Provider>
  );
}

const clamp = (value: number) => Math.max(0, Math.min(1, value));
const ease = (value: number) => {
  const t = clamp(value);
  return t * t * (3 - 2 * t);
};
const out = (value: number) => 1 - (1 - clamp(value)) ** 3;

export function DocumentLoader({
  indeterminate = false,
  ready,
  onComplete,
}: DocumentLoaderProps) {
  const localClock = useRef<MotionState>({
    time: 0,
    completedAt: null,
  });
  const parentClock = useContext(MotionContext);
  // Individual lazy pages must not reuse a completed document's clock.
  const clock = indeterminate ? localClock : (parentClock ?? localClock);
  const [motionState, setMotion] = useState(clock.current);
  // A new load can reuse the animation clock, but not an earlier completion.
  // Apply this before the first frame so a remounted loader is visible at once.
  const motion = ready ? motionState : { ...motionState, completedAt: null };
  const clipId = useId();
  const completionDuration = 0.45;
  const completionFillDuration = 0.3;

  useEffect(() => {
    if (!ready) clock.current = { ...clock.current, completedAt: null };
    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    const start = performance.now();
    let previous = start;
    let frame: number;
    const tick = (now: number) => {
      const dt = Math.max(0, now - previous) / 1000;
      previous = now;
      const time = reduced
        ? Math.max(clock.current.time + dt, 1.8)
        : clock.current.time + dt;
      let completedAt = clock.current.completedAt;
      if (ready && completedAt == null) {
        completedAt = time - dt;
      }
      clock.current = { time, completedAt, reduced };
      setMotion(clock.current);
      if (
        completedAt != null &&
        (reduced || time - completedAt >= completionDuration)
      ) {
        onComplete?.();
        return;
      }
      if (!reduced) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [clock, completionDuration, onComplete, ready]);

  const completionAge =
    motion.completedAt == null ? 0 : motion.time - motion.completedAt;
  // This repeating sweep indicates activity, not progress. Each revolution takes 2s.
  const cyclePhase = ((motion.completedAt ?? motion.time) % 4) / 2;
  const clearing = cyclePhase >= 1 && !motion.reduced;
  // Keep the current colored region, then fill the remaining gray during
  // the fade. Completion must not wait for a full activity revolution.
  const finish = ease(completionAge / completionFillDuration);
  let markFraction = clearing
    ? (cyclePhase - 1) * (1 - finish)
    : cyclePhase + (1 - cyclePhase) * finish;
  if (motion.reduced) markFraction = 1;
  const angle = markFraction * 2 * Math.PI;
  const sector = `M12.0851 12 L12.0851 -6 A18 18 0 ${markFraction > 0.5 ? 1 : 0} 1 ${12.0851 + 18 * Math.sin(angle)} ${12 - 18 * Math.cos(angle)} Z`;
  const paths = sunburstRays.map((d) => {
    let reveal = out(motion.time / 0.3);
    if (motion.completedAt != null) {
      reveal = Math.max(reveal, ease(completionAge / completionFillDuration));
    }
    const scale = 0.8 + 0.2 * reveal;
    return (
      <path
        key={d}
        d={d}
        opacity={reveal}
        transform={`translate(12.0851 12) scale(${scale}) translate(-12.0851 -12)`}
      />
    );
  });
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      height="100%"
      p={3}
      sx={{
        opacity: onComplete
          ? 1 - ease(completionAge / completionDuration) ** 2
          : 1,
      }}
    >
      <svg
        width="48"
        height="48"
        viewBox="0 0 24.1702 24"
        style={{ maxWidth: '100%' }}
        role="status"
        aria-label="Loading document"
      >
        <defs>
          <clipPath id={clipId}>
            {markFraction >= 1 ? (
              <rect x="-6" y="-6" width="36" height="36" />
            ) : (
              <path
                d={clearing ? `M-6 -6 H30 V30 H-6 Z ${sector}` : sector}
                clipRule="evenodd"
              />
            )}
          </clipPath>
        </defs>
        <g fill="#B8BDC5">{paths}</g>
        <g fill="#FF594E" clipPath={`url(#${clipId})`}>
          {paths}
        </g>
      </svg>
    </Stack>
  );
}
